import { useMutation } from "@tanstack/react-query";
import { useDataProvider, useGetOne, useNotify } from "ra-core";
import { useCallback, useState } from "react";

import type { CrmDataProvider } from "../providers/types";
import { billingState, type BillingStatus } from "./billingState";
import { openUrl } from "./openUrl";

/** The single row of billing_status. */
const BILLING_ID = 1;
/** The banner follows a payment within this delay, or when the tab is shown again. */
const REFRESH_MS = 5 * 60 * 1000;

export const useBillingStatus = () =>
  useGetOne<BillingStatus>(
    "billing_status",
    { id: BILLING_ID },
    {
      refetchInterval: REFRESH_MS,
      refetchOnWindowFocus: true,
      retry: false,
    },
  );

/** What to show; nothing is blocked while the status is unknown. */
export const useBillingState = () => {
  const { data } = useBillingStatus();
  return billingState(data ?? null);
};

/**
 * « Régler maintenant » and « Gérer mon abonnement »: the server gives the
 * Stripe page to open (the invoice to pay, the payment link, the portal).
 */
export const useBillingActions = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const [isOpening, setOpening] = useState(false);

  const open = useCallback(
    async (getUrl: () => Promise<string>) => {
      setOpening(true);
      try {
        openUrl(await getUrl());
      } catch {
        notify("crm.billing.errors.unavailable", { type: "error" });
        setOpening(false);
      }
    },
    [notify],
  );

  return {
    isOpening,
    pay: () => open(() => dataProvider.getBillingPaymentUrl()),
    manage: () => open(() => dataProvider.getBillingPortalUrl()),
  };
};

/** Reads the subscription again from Stripe (administrators). */
export const useRefreshBilling = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { refetch } = useBillingStatus();
  const notify = useNotify();
  return useMutation({
    mutationFn: () => dataProvider.refreshBilling(),
    onSuccess: () => {
      refetch();
      notify("crm.billing.refreshed", { type: "success" });
    },
    onError: () => notify("crm.billing.errors.unavailable", { type: "error" }),
  });
};
